import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button } from '@saucelabs/ds-button';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';

import { Actions, Heading, List } from './PopoverComponents';

const MANUAL_STARTER_PRICE = '$39';
const AUTOMATED_STARTER_PRICE = '$149';
const CREDIT_STARTER_PRICE = '$50.00';

const ThreeDays = ({ days, onCheckout, onClose, price }) => {
  const flags = useFlags();

  return (
    <>
      <Heading onClose={onClose}>
        {`Your free trial expires in ${days} days!`}
      </Heading>
      <List checkList={[
        <>
          Need help?
          {' '}
          <Link
            target="_blank"
            to={{ pathname: 'https://saucelabs.com/contact' }}
          >
            Talk to our testing experts
          </Link>
        </>,
        <>
          Get more out of Sauce Labs with
          {' '}
          <Link
            target="_blank"
            to={{ pathname: 'https://training.saucelabs.com' }}
          >
            Sauce School
          </Link>
        </>,
      ]}
      />
      <Actions>
        <Button
          className="text-center justify-center px-3"
          color="primary"
          onClick={onCheckout}
          size="sm"
        >
          {flags?.webUiUsageBasedBilling ? (
            <span className="font-normal">{`Learn about the starter package (${CREDIT_STARTER_PRICE})`}</span>
          ) : (
            <span className="font-normal">
              {`Learn more about our starter plan ${price}/month`}
            </span>
          )}
        </Button>
        {flags?.webUiUsageBasedBilling && (
          <Link
            className="mt-2 mb-0 text-center font-semibold"
            to="/billing/purchase#how-credits-work"
          >
            Read credit FAQs
          </Link>
        )}
      </Actions>
    </>
  );
};

ThreeDays.propTypes = {
  days: PropTypes.number.isRequired,
  onCheckout: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  price: PropTypes.string.isRequired,
};

const OneDay = ({ onCheckout, onClose, price }) => {
  const flags = useFlags();

  return (
    <>
      <Heading onClose={onClose}>
        Get the starter
        {' '}
        {flags?.webUiUsageBasedBilling ? 'package' : 'plan'}
        {' '}
        to keep testing.
      </Heading>
      <Actions>
        <Button
          className="text-center justify-center px-3"
          color="primary"
          onClick={onCheckout}
          size="sm"
        >
          {flags?.webUiUsageBasedBilling ? (
            <span className="font-normal">{`Get starter package (${CREDIT_STARTER_PRICE})`}</span>
          ) : (
            <span className="font-normal">{`Get starter plan ${price}/month`}</span>
          )}
        </Button>
        {flags?.webUiUsageBasedBilling && (
          <Link
            className="mt-2 mb-0 text-center font-semibold"
            to="/billing/purchase#how-credits-work"
          >
            Read credit FAQs
          </Link>
        )}
      </Actions>
    </>
  );
};

OneDay.propTypes = {
  onCheckout: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  price: PropTypes.string.isRequired,
};

/**
 * This popover is only for usage based billing. It will not display if the
 * user is expired and not in the usage based billing cohort
 */
const ZeroDay = ({ onCheckout, onClose }) => (
  <>
    <Heading onClose={onClose}>
      Buy a plan to keep testing.
    </Heading>
    <List
      checkList={[
        'Credits allow you to run automated and manual tests on any combination of desktop browsers and virtual devices',
        'Credits are applied automatically when you run tests',
      ]}
    />
    <Actions>
      <Button
        className="text-center justify-center px-3"
        color="primary"
        onClick={onCheckout}
        size="sm"
      >
        <span className="font-normal">{`Get starter plan (${CREDIT_STARTER_PRICE})`}</span>
      </Button>
      <Link
        className="mt-2 mb-0 text-center font-semibold"
        to="/billing/purchase#how-credits-work"
      >
        Read credit FAQs
      </Link>
    </Actions>
  </>
);

ZeroDay.propTypes = {
  onCheckout: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const POPOVER_CONTENT = {
  3: ThreeDays,
  2: ThreeDays,
  1: OneDay,
  0: ZeroDay,
};

const TrialExpiryPopover = ({ daysToExpiry, onCheckout, onClose, isManual }) => {
  const Content = POPOVER_CONTENT[daysToExpiry];
  if (!Content) {
    onClose();
    return null;
  }

  const price = isManual ? MANUAL_STARTER_PRICE : AUTOMATED_STARTER_PRICE;

  return (
    <div
      className="w-50 max-w-screen bg-white border border-gray-200 border-solid shadow-md rounded p-3 absolute right-0 top-full mt-0.5"
      style={{
        width: 528,
      }}
    >
      <Content
        days={daysToExpiry}
        onCheckout={onCheckout}
        onClose={onClose}
        price={price}
      />
    </div>
  );
};

TrialExpiryPopover.propTypes = {
  daysToExpiry: PropTypes.number,
  isManual: PropTypes.bool,
  onCheckout: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

TrialExpiryPopover.defaultProps = {
  daysToExpiry: 0,
  isManual: false,
  onClose: () => {},
};

export default TrialExpiryPopover;
export { TrialExpiryPopover };
